import { useTwilioPhone } from '@/hooks/useTwilioPhone';
import type { CSSProperties, ReactNode } from 'react';

type SystemType = 'email' | 'url' | 'phone' | string;

export interface SystemLinkProps {
  system?: SystemType;
  value?: string;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}

/**
 * SystemLink wraps its children with the appropriate link or click behavior based on the system/value.
 * - email => mailto: link
 * - url   => absolute http(s) URL (prepends https:// when missing)
 * - phone => triggers Twilio phone call via useTwilioPhone
 * Falls back to a plain wrapper when system/value are not provided or unknown.
 */
export function SystemLink({ system, value, children, className, style }: SystemLinkProps) {
  const phone = useTwilioPhone();

  if (!system || !value) {
    return <>{children}</>; // no behavior
  }

  // Configure handlers
  const onClick: React.MouseEventHandler | undefined =
    system === 'phone'
      ? (e) => {
          e.stopPropagation();
          phone.makeCall(value);
        }
      : undefined;

  const href =
    system === 'email'
      ? `mailto:${value}`
      : system === 'url'
        ? value.startsWith('http')
          ? value
          : `https://${value}`
        : undefined;

  const target = system === 'email' ? '_self' : '_blank';

  // Link case
  if (href) {
    return (
      <a
        href={href}
        target={target}
        rel="noopener noreferrer"
        className={className}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
          ...style,
        }}
      >
        {children}
      </a>
    );
  }

  // Click handler case (e.g., phone)
  if (onClick) {
    return (
      <span
        role="button"
        onClick={onClick}
        className={className}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
          cursor: 'pointer',
          ...style,
        }}
      >
        {children}
      </span>
    );
  }

  // Fallback - no behavior
  return <>{children}</>;
}
